import {
  Box,
  Button,
  ButtonGroup,
  Heading,
  IconButton,
  Menu,
  Portal,
  Table,
  Text,
} from "@chakra-ui/react-v3";
import { Condition, DocumentReference } from "@medplum/fhirtypes";
import dayjs from "dayjs";
import { useMemo } from "react";
import { FaChevronDown } from "react-icons/fa";
import { RiAddLine, RiCheckLine } from "react-icons/ri";
import { Tooltip } from "../../../ui/tooltip";
import { getFirstCodeSpecified } from "../../shared-components/fhir-sections/shared";
import { useDownloadFile } from "../../shared-logic/useDownloadFile";
import { Z_INDEX } from "../constants/z-index";
import { useDocumentCallbackContext, useFhirSdkContext } from "../side-panels/sidepanel-context";
import { getDocIdExtension } from "../utils/document-utils";
import {
  getDateForResourceType,
  getDisplayValueForResourceType,
  getValueForResourceType,
} from "../utils/resource-value-extractors";

/**
 * Minimal interface for resources that can be displayed in the documents table.
 * Resources must have an extension with doc-id and be compatible with
 * getDateForResourceType and getDisplayValueForResourceType utility functions.
 * This is a structural type that Smart<Observation> and other FHIR resources implement.
 */
export type WithExtension = {
  extension?: Array<{
    url?: string;
    valueString?: string;
    valueReference?: { reference?: string };
  }>;
};

type DocumentInfo = {
  docId: string;
  index: number;
  date?: string;
  value?: string;
  resource: WithExtension;
  documentCategoryDisplay?: string;
  documentTypeDisplay?: string;
  facilityTypeDisplay?: string;
  practiceSettingDisplay?: string;
  startTime?: string;
  endTime?: string;
};

type ResourceDocumentsProps = {
  resources: WithExtension[];
  resourceType: string;
};

/**
 * Extracts the search term for PDF based on resource type
 * For Conditions: returns ICD-10 code if available, otherwise the condition name
 */
function getSearchTermForResource(resourceType: string, resources: unknown[]): string | undefined {
  if (resourceType !== "Condition" || resources.length === 0) {
    return undefined;
  }

  const firstResource = resources[0] as unknown as Condition;

  /** Try to get ICD-10 code first */
  const coding = getFirstCodeSpecified(firstResource.code?.coding ?? [], ["icd-10"]);
  if (coding?.code) {
    return coding.code;
  }

  /** Fallback to condition name */
  return firstResource.code?.text || firstResource.code?.coding?.[0]?.display;
}

/**
 * Gets title and description for service date
 * Title is always the primary date, description provides time or end date context
 */
function getServiceDateInfo(
  startTime?: string,
  endTime?: string,
  fallbackDate?: string
): { title: string; description?: string } {
  const dateToUse = startTime ?? endTime ?? fallbackDate;
  if (!dateToUse) {
    return { title: "Unknown" };
  }

  const primaryDate = dayjs(dateToUse);
  const title = primaryDate.format("MMM D, YYYY");

  // Same-day service with time range
  if (startTime && endTime) {
    const start = dayjs(startTime);
    const end = dayjs(endTime);

    if (start.isSame(end, "day")) {
      return {
        title,
        description: `${start.format("h:mm A")} - ${end.format("h:mm A")}`,
      };
    }

    // Multi-day service
    return {
      title,
      description: `→ ${end.format("MMM D, YYYY")}`,
    };
  }

  // Single timestamp
  if (startTime) {
    return {
      title,
      description: dayjs(startTime).format("h:mm A"),
    };
  }

  if (endTime) {
    return {
      title,
      description: dayjs(endTime).format("h:mm A"),
    };
  }

  // Date only, no time
  return { title };
}

/**
 * Gets title and description for document type (category as description, type as title)
 */
function getDocumentTypeInfo(
  categoryDisplay?: string,
  typeDisplay?: string
): { title?: string; description?: string } {
  if (!categoryDisplay && !typeDisplay) {
    return { title: "Document" };
  }

  if (!categoryDisplay) {
    return { title: typeDisplay };
  }

  if (!typeDisplay) {
    return { title: categoryDisplay };
  }

  if (categoryDisplay === typeDisplay) {
    return { title: categoryDisplay };
  }

  // Type is more specific, so use as title; category as description
  return { title: typeDisplay, description: categoryDisplay };
}

/**
 * Gets title and description for facility (facility type as title, practice setting as description)
 */
function getFacilityInfo(
  facilityType?: string,
  practiceSetting?: string
): { title?: string; description?: string } {
  if (!facilityType && !practiceSetting) {
    return {};
  }

  if (!facilityType) {
    return { title: practiceSetting };
  }

  if (!practiceSetting) {
    return { title: facilityType };
  }

  if (facilityType === practiceSetting) {
    return { title: facilityType };
  }

  // Facility type is broader, practice setting is more specific
  return { title: facilityType, description: practiceSetting };
}

/**
 * Renders a service date cell with title/description format
 */
function ServiceDateCell({ info }: { info: { title: string; description?: string } }) {
  if (!info.title) {
    return (
      <Text color="fg.muted" fontSize="sm">
        -
      </Text>
    );
  }

  return (
    <Box>
      <Text color="fg.emphasized" fontSize="sm">
        {info.title}
      </Text>
      {info.description && (
        <Text fontSize="sm" color="fg.placeholder" mt={0.5}>
          {info.description}
        </Text>
      )}
    </Box>
  );
}

/**
 * Renders a document type cell with title/description format
 */
function DocumentTypeCell({ info }: { info: { title?: string; description?: string } }) {
  if (!info.title) {
    return (
      <Text color="fg.muted" fontSize="sm">
        -
      </Text>
    );
  }

  return (
    <Box>
      <Text color="fg.emphasized" fontSize="sm">
        {info.title}
      </Text>
      {info.description && (
        <Text fontSize="sm" color="fg.placeholder" mt={0.5}>
          {info.description}
        </Text>
      )}
    </Box>
  );
}

/**
 * Renders a facility cell with title/description format
 * Uses text truncation to keep column width manageable
 */
function FacilityCell({ info }: { info: { title?: string; description?: string } }) {
  if (!info.title) {
    return (
      <Text color="fg.muted" fontSize="sm">
        -
      </Text>
    );
  }

  return (
    <Box maxWidth="100%">
      <Text
        color="fg.emphasized"
        fontSize="sm"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        display="block"
        title={info.title}
      >
        {info.title}
      </Text>
      {info.description && (
        <Text
          fontSize="sm"
          color="fg.placeholder"
          mt={0.5}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          display="block"
          title={info.description}
        >
          {info.description}
        </Text>
      )}
    </Box>
  );
}

/**
 * Renders a value cell with text truncation
 */
function ValueCell({ value }: { value?: string }) {
  if (!value) {
    return (
      <Text color="fg.muted" fontSize="sm">
        -
      </Text>
    );
  }

  return (
    <Text
      color="fg.emphasized"
      fontSize="sm"
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
      display="block"
      title={value}
    >
      {value}
    </Text>
  );
}

/**
 * Compact toggle button for marking/unmarking documents for export
 */
function MarkForExportButton({ isMarked, onClick }: { isMarked: boolean; onClick: () => void }) {
  if (isMarked) {
    return (
      <Tooltip content="Remove from export list" positioning={{ placement: "top" }}>
        <IconButton
          size="sm"
          variant="solid"
          bg="btn.success.bg"
          color="btn.success.color"
          borderColor="btn.success.bg"
          _hover={{
            backgroundColor: "btn.success.hover.bg",
            borderColor: "btn.success.hover.bg",
          }}
          onClick={onClick}
          aria-label="Remove from export"
        >
          <RiCheckLine />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip content="Mark this document for export" positioning={{ placement: "top" }}>
      <IconButton
        size="sm"
        variant="outline"
        bg="transparent"
        color="fg.muted"
        borderColor="border.subtle"
        _hover={{
          backgroundColor: "bg.muted",
          borderColor: "border.emphasized",
        }}
        onClick={onClick}
        aria-label="Mark for export"
      >
        <RiAddLine />
      </IconButton>
    </Tooltip>
  );
}

/**
 * Component that displays download buttons for all documents associated with resources.
 * Works with both series and single resources.
 */
export function ResourceDocuments({ resources, resourceType }: ResourceDocumentsProps) {
  const fhirSdk = useFhirSdkContext();
  const { onAddToExport, onRemoveFromExport, isDocumentInExportList, onPdfView } =
    useDocumentCallbackContext();
  const { onDownloadFile, isDownloading } = useDownloadFile();

  const docRefLookup = useMemo(
    () =>
      fhirSdk.getDocumentReferences().reduce((acc, doc) => {
        const docId = doc.meta?.source;
        if (docId === undefined) return acc;
        return { ...acc, [docId]: doc };
      }, {} as { [key: string]: DocumentReference }),
    [fhirSdk]
  );

  // Extract all documents from the resources, showing each data point
  const documents: DocumentInfo[] = useMemo(() => {
    const documentsList: DocumentInfo[] = [];

    resources.forEach((resource, index) => {
      const docId = getDocIdExtension(resource);
      if (!docId) return;

      const date = getDateForResourceType(resource);
      const value = getDisplayValueForResourceType(resource) || getValueForResourceType(resource);

      const docRef = docRefLookup[docId] ?? ({} as DocumentReference);

      const documentCategoryDisplay = docRef.category?.[0]?.coding?.[0]?.display;
      const documentTypeDisplay = docRef.type?.coding?.[0]?.display;
      const facilityTypeDisplay = docRef.context?.facilityType?.coding?.[0]?.display;
      const practiceSettingDisplay = docRef.context?.practiceSetting?.coding?.[0]?.display;
      const startTime = docRef.context?.period?.start;
      const endTime = docRef.context?.period?.end;

      documentsList.push({
        docId,
        index,
        date,
        value: value?.toString(),
        resource,
        documentCategoryDisplay,
        documentTypeDisplay,
        facilityTypeDisplay,
        practiceSettingDisplay,
        startTime,
        endTime,
      });
    });

    // Sort by service period start time (preferred) or fallback date, descending (newest first)
    return documentsList.sort((a, b) => {
      const aTime = a.startTime ?? a.date;
      const bTime = b.startTime ?? b.date;
      if (!aTime || !bTime) return 0;
      return dayjs(bTime).diff(dayjs(aTime));
    });
  }, [resources, docRefLookup]);

  if (documents.length === 0) {
    return null;
  }

  function handleDownload(docId: string, conversionType?: "html" | "pdf") {
    onDownloadFile({
      docId,
      docFilename: docId,
      conversionType,
    });
  }

  function handlePdfView(docId: string) {
    const searchTerm = getSearchTermForResource(resourceType, resources);
    onPdfView(docId, docId, searchTerm);
  }

  function handleToggleExport(docId: string) {
    if (isDocumentInExportList(docId)) {
      onRemoveFromExport(docId);
    } else {
      onAddToExport(docId, docId);
    }
  }

  return (
    <Box>
      <Heading size="md" mb={4} color="fg.emphasized">
        Source Documents ({documents.length})
      </Heading>
      <Box borderWidth="1px" borderColor="border.emphasized" borderRadius="md" overflow="auto">
        <Table.Root size="sm" variant="line">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader width="140px">Service Date</Table.ColumnHeader>
              <Table.ColumnHeader minWidth="200px">Document Type</Table.ColumnHeader>
              <Table.ColumnHeader width="180px" maxWidth="180px">
                Facility
              </Table.ColumnHeader>
              <Table.ColumnHeader width="150px" maxWidth="150px">
                Value
              </Table.ColumnHeader>
              <Table.ColumnHeader width="180px" textAlign="left">
                Actions
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {documents.map(doc => {
              const serviceDateInfo = getServiceDateInfo(doc.startTime, doc.endTime, doc.date);
              const documentTypeInfo = getDocumentTypeInfo(
                doc.documentCategoryDisplay,
                doc.documentTypeDisplay
              );
              const facilityInfo = getFacilityInfo(
                doc.facilityTypeDisplay,
                doc.practiceSettingDisplay
              );

              return (
                <Table.Row key={`${doc.docId}-${doc.index}`}>
                  <Table.Cell width="140px">
                    <ServiceDateCell info={serviceDateInfo} />
                  </Table.Cell>
                  <Table.Cell minWidth="200px">
                    <DocumentTypeCell info={documentTypeInfo} />
                  </Table.Cell>
                  <Table.Cell width="180px" maxWidth="180px">
                    <FacilityCell info={facilityInfo} />
                  </Table.Cell>
                  <Table.Cell width="150px" maxWidth="150px" fontSize="sm">
                    <ValueCell value={doc.value} />
                  </Table.Cell>

                  {/* Actions Column */}
                  <Table.Cell width="180px" textAlign="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <MarkForExportButton
                        isMarked={isDocumentInExportList(doc.docId)}
                        onClick={() => handleToggleExport(doc.docId)}
                      />
                      <Menu.Root positioning={{ placement: "bottom-end" }}>
                        <ButtonGroup size="sm" variant="outline" attached>
                          <Button
                            variant="outline"
                            _hover={{ backgroundColor: "bg.muted" }}
                            onClick={() => handlePdfView(doc.docId)}
                          >
                            View PDF
                          </Button>
                          <Menu.Trigger asChild>
                            <IconButton
                              variant="outline"
                              borderLeftRadius={0}
                              _hover={{ backgroundColor: "bg.muted" }}
                            >
                              <FaChevronDown />
                            </IconButton>
                          </Menu.Trigger>
                        </ButtonGroup>
                        <Portal>
                          <Menu.Positioner>
                            <Menu.Content zIndex={Z_INDEX.SIDE_PANEL_RESOURCE_DOCUMENTS_MENU}>
                              <Menu.Item
                                value="html"
                                onClick={() => handleDownload(doc.docId, "html")}
                                cursor="pointer"
                                disabled={
                                  isDownloading[doc.docId]?.downloading &&
                                  isDownloading[doc.docId]?.type === "html"
                                }
                              >
                                {isDownloading[doc.docId]?.downloading &&
                                isDownloading[doc.docId]?.type === "html"
                                  ? "Downloading HTML..."
                                  : "Download HTML"}
                              </Menu.Item>
                              <Menu.Item
                                value="xml"
                                onClick={() => handleDownload(doc.docId, undefined)}
                                cursor="pointer"
                                disabled={
                                  isDownloading[doc.docId]?.downloading &&
                                  isDownloading[doc.docId]?.type === "xml"
                                }
                              >
                                {isDownloading[doc.docId]?.downloading &&
                                isDownloading[doc.docId]?.type === "xml"
                                  ? "Downloading XML..."
                                  : "Download XML"}
                              </Menu.Item>
                            </Menu.Content>
                          </Menu.Positioner>
                        </Portal>
                      </Menu.Root>
                    </Box>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Box>
      <Text fontSize="xs" color="fg.muted" mt={2}>
        Download source documents in different formats. HTML and PDF conversions may take up to 30
        seconds.
      </Text>
    </Box>
  );
}
